import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For deeper gloss and lasting protection, pair it with our ceramic coating service.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We are fully self-contained with our own spot-free water and power, so we can detail your vehicle anywhere in Ventura County.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-603" data-mp="28c9brw617mk4">
      <p data-id="element-604" data-mp="1klloo3njoqcz">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-605" data-mp="3azmb19fk3c8n">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-606" data-mp="1agx0lvow0s4b">Our Exterior Detailing Process</h2>
      <p data-id="element-607" data-mp="drxpm8wx5c8f">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-608" data-mp="msm414q85elu">
        <li className="flex items-start" data-id="element-609" data-mp="mrueobn80d40">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-610" data-mp="rrp7wlugxes8" />
          <span data-id="element-611" data-mp="rt0eetfat0n1">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-612" data-mp="235154dvq2z5f">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-613" data-mp="21pgl0pm72nl5" />
          <span data-id="element-614" data-mp="21rt3d3vv6zyq">
            <strong data-id="element-615" data-mp="1wqlvl4tq20s2">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-616" data-mp="26ojr8ddwuzup">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-617" data-mp="26ni7lkitx51d" />
          <span data-id="element-618" data-mp="2kn9pvxgdp7l6">
            <strong data-id="element-619" data-mp="2kmzutvgh8a8m">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-620" data-mp="2perern54nhps">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-621" data-mp="2pft1iovi9snc" />
          <span data-id="element-622" data-mp="33eaoulhi134q">
            <strong data-id="element-623" data-mp="33fu5id72hg71">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-624" data-mp="3i2an28qc9xvd">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-625" data-mp="3n0lxologkcen" />
          <span data-id="element-626" data-mp="3myrm3xz2twn8">
            <strong data-id="element-627" data-mp="39132suknqxka">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-628" data-mp="2za8dtig11jv5">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-629" data-mp="2z96u6pky3p1t" />
          <span data-id="element-630" data-mp="343kpyt3iry70">
            <strong data-id="element-631" data-mp="3442vkgc2norl">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-632" data-mp="2gcnq4ep00rc4">Why Exterior Detailing is Essential</h2>
      <p data-id="element-633" data-mp="2gcxfq164swha">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-634" data-mp="19t34u3y5d4zu">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-635" data-mp="14vb3kjbm46iz">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and electricity, meaning we can perform a
        flawless exterior detail in your driveway, at your office parking lot,
        or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-636" data-mp="1w3cn23bbazlk">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-637" data-mp="1w0gme782xwtf">
        <Link to="/services/ceramic-coating" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-638" data-mp="2125zdsev4kk5">
          Ceramic Coating &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-639" data-mp="2etb2j4hfvuz8">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-640" data-mp="2orqiqcexhjty">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}